import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";

/**
 * AddManualCreditModal — books a disposal/write-off the client's books
 * never captured as a credit-side voucher on an FA ledger. No Tally ledger
 * to derive the block from, so the auditor picks it directly. Lands as
 * "pending" — classify Sale/Discount/Depreciation via the existing row
 * buttons afterward.
 */
export function AddManualCreditModal({ blocks, onSave, onClose }) {
  const [form, setForm] = useState({
    block_label: "",
    particulars: "",
    party_name: "",
    accounting_date: "",
    amount: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSave = form.block_label && form.particulars.trim() && form.amount !== "";

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave({ ...form, amount: parseFloat(form.amount) || 0 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
         data-testid="fa-add-manual-credit-modal">
      <div className="bg-white border border-[#E5E5E0] w-full max-w-lg">
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-[#EDEDE7]">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-600">Credit Entries</div>
            <div className="font-heading text-base mt-0.5">Add New Row (Manual Entry)</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1" data-testid="fa-add-manual-credit-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">IT Block *</label>
            <select
              value={form.block_label}
              onChange={set("block_label")}
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-add-manual-credit-block"
            >
              <option value="">— Select block —</option>
              {(blocks || []).map((b) => (
                <option key={b.block_label} value={b.block_label}>{b.block_label}</option>
              ))}
            </select>
            <div className="text-[10.5px] text-slate-500 mt-1">
              No Tally ledger behind a manual credit, so pick the block directly.
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Particulars *</label>
            <input
              value={form.particulars}
              onChange={set("particulars")}
              placeholder="e.g. Sale of old Generator Set"
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-add-manual-credit-particulars"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Party Name</label>
              <input
                value={form.party_name}
                onChange={set("party_name")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-credit-party"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Date</label>
              <input
                type="date"
                value={form.accounting_date}
                onChange={set("accounting_date")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-credit-date"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Cr Amount (₹) *</label>
            <input
              type="text" inputMode="decimal"
              value={form.amount}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^-?\d*\.?\d*$/.test(v)) setForm((f) => ({ ...f, amount: v }));
              }}
              placeholder="0.00"
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700 text-right font-mono"
              data-testid="fa-add-manual-credit-amount"
            />
          </div>
          <div className="text-[10.5px] text-slate-500">
            Classify as Sale / Discount / Depreciation on the row after saving, same as any other credit.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#EDEDE7] bg-[#FBFBF8]">
          <button onClick={onClose} className="px-3 py-1.5 text-[12.5px] border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button
            data-testid="fa-add-manual-credit-save"
            onClick={save}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving && <Loader2 size={12} className="animate-spin"/>} Add Row
          </button>
        </div>
      </div>
    </div>
  );
}
